/**
 * The converter publishes `features.json` next to `tileset.json`. This module
 * knows its shape (v2: full property bag, bbox, spatial container, hierarchy) and
 * how to find and fetch it. Pure: `fetch` is injectable.
 */

export type SidecarFeature = {
	featureId: number;
	handle?: string;
	layer?: string;
	kind?: string;
	nodeId?: string | null;
	bbox?: number[] | null;
	properties?: Record<string, string>;
};

export type SidecarNode = {
	nodeId: string;
	parentId: string | null;
	kind: string;
	name: string;
	elevation: number | null;
};

export type SidecarFile = {
	count?: number;
	features: SidecarFeature[];
	hierarchy?: SidecarNode[];
};

export class SidecarError extends Error {}

const TILESET_FILE = 'tileset.json';
const SIDECAR_FILE = 'features.json';

/**
 * `.../tileset.json?v=1` -> `.../features.json?v=1`. The query (cache-busting
 * version) is kept so a reposition refetches the sidecar with the tileset.
 * Returns null when the URL does not point at a tileset.json.
 */
export function sidecarUrlFor(tilesetUrl: string): string | null {
	const queryStart = tilesetUrl.indexOf('?');
	const path = queryStart === -1 ? tilesetUrl : tilesetUrl.slice(0, queryStart);
	const query = queryStart === -1 ? '' : tilesetUrl.slice(queryStart);
	if (!path.endsWith(TILESET_FILE)) return null;
	return path.slice(0, -TILESET_FILE.length) + SIDECAR_FILE + query;
}

export function parseSidecar(raw: unknown): SidecarFile {
	if (!raw || typeof raw !== 'object') throw new SidecarError('Sidecar is not an object');
	const obj = raw as Record<string, unknown>;
	if (!Array.isArray(obj.features)) throw new SidecarError('Sidecar has no features array');
	return {
		count: typeof obj.count === 'number' ? obj.count : undefined,
		features: obj.features as SidecarFeature[],
		hierarchy: Array.isArray(obj.hierarchy) ? (obj.hierarchy as SidecarNode[]) : undefined,
	};
}

export async function fetchSidecar(
	url: string,
	signal?: AbortSignal,
	fetchImpl: typeof fetch = fetch
): Promise<SidecarFile> {
	const response = await fetchImpl(url, { signal });
	if (!response.ok) throw new SidecarError(`Sidecar request failed: ${response.status}`);
	let body: unknown;
	try {
		body = await response.json();
	} catch {
		throw new SidecarError('Sidecar is not valid JSON');
	}
	return parseSidecar(body);
}
